import { useQuery } from "react-query";
import { useAuth } from "../../hooks/useAuth";
import { Button, Form, Modal, Table, Spinner, Alert } from "react-bootstrap";
import { fetchActionsUtil, formatDate } from "../../utils/helpers";
import CachedIcon from "mdi-react/CachedIcon";
import CreatableSelect from "react-select/creatable";
import { useState } from "react";
import { useFormik } from "formik";
import FilledFormSelectorModal from "../CustomersEmail/FilledFormSelectorModal";
import FormPreviewModal from "../Requisition/FormPreviewModal";
import { TrashIcon } from "../Icons";
import def from "ajv/dist/vocabularies/discriminator";
import { isEmpty } from "lodash";

/**
 * OperatorCertificationModal
 *
 * Props:
 *  - show           {boolean}   whether the modal is visible
 *  - onHide         {function}  called when the modal should close
 *  - Bar_Code       {string}    asset bar-code; drives the certifications fetch
 *  - users          {array}     operator options for CreatableSelect
 *  - onSuccess      {function}  optional callback after a successful POST
 *  - defaultValues  {object}    preloaded form data:
 *                               { operator, operatorStaff_ID, certification }
 */
export default function OperatorCertificationModal({
  show,
  onHide,
  Bar_Code,
  users = [],
  onSuccess,
  defaultValues = {},
}) {
  const { backendUrl, token } = useAuth();

  // ── child modal state ────────────────────────────────────────────────────
  const [showChildForm, setShowChildForm] = useState({
    show: false,
    certIndex: null,
    certificationId: null,
  });

  const [showFormPreviewModal, setShowFormPreviewModal] = useState({
    show: false,
    selectedFilledForm: null,
  });

  // ── submission state ─────────────────────────────────────────────────────
  const [submitStatus, setSubmitStatus] = useState(null); // null | 'loading' | 'success' | 'error'
  const [submitError, setSubmitError] = useState("");

  // ── helpers ───────────────────────────────────────────────────────────────
  const normalizeArrayPayload = payload => {
    if (Array.isArray(payload)) return payload;
    if (payload && typeof payload === "object") {
      if (Array.isArray(payload.data)) return payload.data;
      if (payload.data && Array.isArray(payload.data.assetRequirements))
        return payload.data.assetRequirements;
      if (payload.data && Array.isArray(payload.data.rows))
        return payload.data.rows;
      if (Array.isArray(payload.assetRequirements))
        return payload.assetRequirements;
      if (Array.isArray(payload.rows)) return payload.rows;
    }
    return [];
  };

  // ── queries ───────────────────────────────────────────────────────────────
  const fetchCertificationTypes = async () => {
    const res = await fetch(
      `${backendUrl}/api/partnership/certification-types`,
      {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      }
    );
    if (!res.ok) throw new Error("Failed to fetch certification types.");
    const payload = await res.json();
    return normalizeArrayPayload(payload).map(item => ({
      value:
        item.certificationId || item.id || item.value || item.code || item.name,
      label:
        item.certificationName ||
        item.name ||
        item.label ||
        item.certificationId ||
        item.id,
      formId: item.formId || item.form_id || null,
    }));
  };

  const fetchCertifications = async () => {
    const res = await fetch(
      `${backendUrl}/api/asset-requirements?Bar_Code=${Bar_Code}`,
      {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        credentials: "include",
      }
    );
    if (!res.ok) throw new Error("Failed to fetch certifications.");
    return normalizeArrayPayload(await res.json());
  };

  const fetchSetUpData = async () => {
    let response = await fetchActionsUtil(
      `${backendUrl}/api/warehouse/issuer-setup/all`,
      "GET"
    );

    const { data } = response;

    data.staff = data.staffs.map(el => ({
      value: el.Name,
      label: el.Name,
    }));

    data.userRequesters = data.userRequesters.map(el => ({
      ...el,
      value: el.name,
      label: el.name,
    }));

    const mergedRequesters = [...data.staff, ...data.userRequesters];
    const uniqueRequestersMap = new Map();

    mergedRequesters.forEach(req => {
      uniqueRequestersMap.set(req.value, req);
    });

    data.userRequesters = Array.from(uniqueRequestersMap.values());

    // console.log("Fetched SetUpData:", {
    //   staff: data.staff,
    //   userRequesters: data.userRequesters,
    // });

    return data;
  };

  const {
    data: certifications = [],
    isLoading,
    isFetching,
    refetch,
  } = useQuery(["asset-certifications", Bar_Code], fetchCertifications, {
    enabled: !!Bar_Code && show,
    refetchOnWindowFocus: false,
  });

  const {
    data: certificationTypeOptions = [],
    refetch: refetchCertificationTypes,
  } = useQuery(["certification-types"], fetchCertificationTypes, {
    enabled: show,
    refetchOnWindowFocus: false,
  });

  const {
    data: { userRequesters = [] } = { userRequesters: [] },
    refetch: refetchSetUpData,
    isLoading: isLoadingSetUpData,
  } = useQuery(["WAREHOUSE_ISSUE_ITEMS"], () => fetchSetUpData(), {
    keepPreviousData: false,
    staleTime: 0,
  });

  const getCertification = certificationId =>
    certificationTypeOptions.find(
      o => String(o.value) === String(certificationId)
    ) ?? null;

  // ── formik ────────────────────────────────────────────────────────────────
  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      operator: defaultValues.operator ?? null,
      operatorStaff_ID: defaultValues.operatorStaff_ID ?? null,
      certification: defaultValues.certification ?? [],
    },
    onSubmit: async values => {
      setSubmitStatus("loading");
      setSubmitError("");
      try {
        const res = await fetch(
          `${backendUrl}/api/asset-requirements/operator-certification`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            credentials: "include",
            body: JSON.stringify({
              Bar_Code,
              operator: values.operator,
              operatorStaff_ID: values.operatorStaff_ID,
              certification: values.certification,
              Trans_ID: defaultValues.Trans_ID, // Include Trans_ID for updates;
            }),
          }
        );

        if (!res.ok) {
          const errBody = await res.json().catch(() => ({}));
          throw new Error(errBody.message || `Server error: ${res.status}`);
        }

        setSubmitStatus("success");
        onSuccess?.();

        // Auto-close after short delay so user sees success feedback
        setTimeout(() => {
          setSubmitStatus(null);
          onHide();
        }, 1200);
      } catch (err) {
        setSubmitStatus("error");
        setSubmitError(err.message || "Submission failed.");
      }
    },
  });

  // ── render ────────────────────────────────────────────────────────────────
  return (
    <>
      <Modal show={show} onHide={onHide} size="lg" centered animation={false}>
        <Modal.Header closeButton>
          <Modal.Title>Operator Certification - {Bar_Code}</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {/* Status banners */}
          {submitStatus === "success" && (
            <Alert variant="success">Saved successfully!</Alert>
          )}
          {submitStatus === "error" && (
            <Alert
              variant="danger"
              onClose={() => setSubmitStatus(null)}
              dismissible
            >
              {submitError}
            </Alert>
          )}

          <Form onSubmit={formik.handleSubmit} id="operator-cert-form">
            {/* Operator selector */}
            <Form.Group className="mb-3">
              <Form.Label className="d-flex align-items-center gap-2">
                Operator
                <Button
                  variant=""
                  size="sm"
                  onClick={() => {
                    refetch();
                    refetchCertificationTypes();
                    refetchSetUpData();
                  }}
                  title="Refresh"
                >
                  <CachedIcon />
                </Button>
              </Form.Label>

              <CreatableSelect
                isClearable
                onChange={selected => {
                  formik.setFieldValue("operator", selected?.value ?? null);
                  formik.setFieldValue(
                    "operatorStaff_ID",
                    selected?.Staff_ID ?? null
                  );
                }}
                options={userRequesters}
                value={{
                  label: formik.values.operator || null,
                  value: formik.values.operator || null,
                  Staff_ID: formik.values.operatorStaff_ID || null,
                }}
              />
            </Form.Group>

            {/* Certifications table */}
            <Table
              striped
              borderless
              hover
              responsive
              className="product-table mb-0 border"
            >
              <thead>
                <tr>
                  <th>Certification</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {certifications.length === 0 && (
                  <tr>
                    <td colSpan={3} className="text-center text-muted py-4">
                      {isLoading || isFetching
                        ? "Loading certifications..."
                        : "No certifications found."}
                    </td>
                  </tr>
                )}

                {certifications.map((cert, index) => (
                  <tr key={cert.id}>
                    <td>
                      <b
                        className="d-block"
                        title={getCertification(cert.certificationId)?.formId}
                      >
                        {getCertification(cert.certificationId)?.label ?? "—"}
                      </b>
                      <i className="text-nowrap">
                        {cert.expiresAt ? (
                          formatDate(cert.expiresAt)
                        ) : (
                          <span className="text-muted">-</span>
                        )}
                      </i>
                    </td>

                    <td>
                      <div>
                        {formik.values.certification?.[index]?.filledForms?.map(
                          (item, formIndex) => (
                            <div
                              className="d-flex gap-2 text-nowrap mb-3"
                              key={formIndex}
                            >
                              <Button
                                size="sm"
                                title={`${item.id}`}
                                variant="link"
                                onClick={() =>
                                  setShowFormPreviewModal({
                                    show: true,
                                    selectedFilledForm: item,
                                  })
                                }
                              >
                                View: {item.formName} - {item.UserName}
                              </Button>

                              <Button
                                size="sm"
                                variant="outline-danger"
                                onClick={() => {
                                  const updatedForms = formik.values.certification[
                                    index
                                  ].filledForms.filter(
                                    (_, i) => i !== formIndex
                                  );
                                  formik.setFieldValue(
                                    `certification.${index}.filledForms`,
                                    updatedForms
                                  );
                                }}
                              >
                                <TrashIcon />
                              </Button>
                            </div>
                          )
                        )}
                      </div>

                      <Button
                        size="sm"
                        variant="light-blue"
                        onClick={() =>
                          setShowChildForm({
                            show: true,
                            certIndex: index,
                            certificationId: cert.certificationId,
                          })
                        }
                      >
                        Select Filled Form
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Form>
        </Modal.Body>

        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={onHide}
            disabled={submitStatus === "loading"}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            form="operator-cert-form"
            variant="primary"
            disabled={submitStatus === "loading"}
          >
            {submitStatus === "loading" ? (
              <>
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                  className="me-2"
                />
                Saving…
              </>
            ) : (
              "Save"
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Child: FilledFormSelectorModal */}
      {showChildForm.show && (
        <FilledFormSelectorModal
          showchildForm={showChildForm.show}
          setShowChildForm={value =>
            setShowChildForm(prev => ({ ...prev, show: value }))
          }
          handleSelectedForms={forms => {
            const updatedCertification = [
              ...(formik.values?.certification || []),
            ];
            const existingForms =
              updatedCertification[showChildForm.certIndex]?.filledForms || [];

            const newForms = forms.map(f => ({
              formName: f.formName,
              UserName: f.UserName,
              FormID: f.FormID,
              id: f.id,
            }));

            const existingIds = new Set(existingForms.map(f => f.id));
            const mergedForms = [
              ...existingForms,
              ...newForms.filter(f => !existingIds.has(f.id)),
            ];

            updatedCertification[showChildForm.certIndex] = {
              ...(updatedCertification[showChildForm.certIndex] || {}),
              filledForms: mergedForms,
            };

            // ✅ THIS LINE WAS MISSING
            formik.setFieldValue("certification", updatedCertification);
            setShowChildForm(prev => ({ ...prev, show: false }));
          }}
          defaultSelectedFormTemplate={{
            label:
              getCertification(showChildForm.certificationId)?.label ||
              "Select Form",
            value:
              getCertification(showChildForm.certificationId)?.formId || null,
          }}
        />
      )}

      {/* Child: FormPreviewModal */}
      {showFormPreviewModal.show && showFormPreviewModal.selectedFilledForm && (
        <FormPreviewModal
          showFormPreviewModal={showFormPreviewModal.show}
          setShowFormPreviewModal={value =>
            setShowFormPreviewModal(prev => ({ ...prev, show: value }))
          }
          template={showFormPreviewModal.selectedFilledForm.filledFormData}
          selectedFilledForm={showFormPreviewModal.selectedFilledForm}
          setSelectedFilledForm={value =>
            setShowFormPreviewModal(prev => ({
              ...prev,
              selectedFilledForm: value,
            }))
          }
        />
      )}
    </>
  );
}
